import { create } from "zustand";
import {
  FetchWizardQry,
  FetchWizardStepQuestionnaire,
  ProjectCommissioningCalcQry,
  TopologyCopyAction,
} from "api/queries";
import {
  AddWizardMutation,
  createWizardStepQuestionnaire,
  deleteWizardStepQuestionnaireMut,
  duplicateWizardStepQuestionnaireMut,
  TopologyActionsMut,
  updateWizardStepQuestionnaireMut,
  WizardStepMut,
  WizardUpdateDetailsMut,
  WizardUpdateZoneMut,
} from "api/mutations";
import NiceModal from "@ebay/nice-modal-react";
import { getTopology } from "objs/_ConfiguratorObjs/Wizard/wizardHelpers";
import { fetchData, translate } from "hooks";
import { isWizardRootFolder } from "utils/treeUtils";
import { getComputedLayout } from "components/Topology/computedLayout/getComputedLayout";
import { ConfirmationModal } from "../../../../components/_Modals/ConfirmationModal";

const showConfirmModal = ({
  action,
  onCancelAction,
  iconName = "save-regular",
  title,
  specificAlertText,
  specificSubmitText,
  specificCancelText,
  isWithoutCancelBtn,
}) => {
  NiceModal.show(ConfirmationModal, {
    iconName,
    title: title || translate("WIZARD_RESET_DATA_CONFIRMATION"),
    alertText:
      specificAlertText || translate("WIZARD_RESET_DATA_CONFIRMATION_ALERT"),
    submitBtnText:
      specificSubmitText || translate("WIZARD_RESET_DATA_CONFIRMATION_ACCEPT"),
    cancelBtnText:
      specificCancelText || translate("WIZARD_RESET_DATA_CONFIRMATION_CANCEL"),
    isWithoutCancelBtn,
    onConfirm: async () => action(),
    onCancel: () => onCancelAction && onCancelAction(),
  });
};

function insertItemAndUpdatePositions(arr, newItems) {
  const clonedZonesIds = new Set(newItems.map((item) => item.id));

  const minPositionOfClonedZone = Math.min(
    ...newItems.map((item) => item.position),
  );

  const existing = arr
    .filter((item) => !clonedZonesIds.has(item.id))
    .map((item) => ({
      ...item,
      position:
        item.position >= minPositionOfClonedZone
          ? item.position + newItems.length
          : item.position,
    }));

  const merged = [...existing, ...newItems].sort(
    (a, b) => a.position - b.position,
  );

  return merged;
}

function removeItemAndUpdatePositions({ arr, itemId }) {
  const itemIndex = arr.findIndex((item) => item.id === itemId);

  if (itemIndex === -1) {
    return arr;
  }

  const itemPosition = arr[itemIndex].position;

  return arr
    .filter((item) => item.id !== itemId)
    .map((item) => {
      if (item.position > itemPosition) {
        return { ...item, position: item.position - 1 };
      }
      return item;
    });
}

function updateTopologyZoneTitle(topology, zoneId, newTitle) {
  if (!Array.isArray(topology)) {
    return topology;
  }

  const deepCopyAndUpdate = (items) =>
    items.map((item) => {
      const newItem = { ...item };

      if (newItem.kind === "fam" && newItem.id === zoneId) {
        newItem.info = newTitle;
      }

      if (Array.isArray(newItem.configurations)) {
        newItem.configurations = deepCopyAndUpdate(newItem.configurations);
      }

      return newItem;
    });

  return deepCopyAndUpdate(topology);
}

const initialState = {
  error: null,
  language: "de",
  projectId: null,
  nodeId: null,
  subType: null,
  structuredTopology: null,
  computedLayoutTopology: null,
  step: null,
  steps: null,
  availableSteps: null,
  constraints: [],
  currentQuestionnaire: null,
  isFetching: false,
  isDuplicationQuestionnaireFetching: false,
  nodes: [],
  wizardTitle: "",
  questionnaireLists: [],
  topology: null,
  interactiveElementsArr: [],
  wizardQuestionsValidation: {
    isValidationMessageShown: true,
  },
  isFirstSideBarOpen: false,
  isSecondSideBarOpen: false,
  selectedTopologyItemId: null,
  selectedTopologyItem: null,
  selectedPasteItem: null,
  famDaisyChainFullness: [],
  topologyOperation: null,
  centerTopologyItemId: null,
  isFormDirty: false,
  wizardResultTab: "topology",
  possibleTopologyActions: [],
  projectCommissioningCost: {
    price: null,
    currency: "",
  },
};

const useWizardStore = create((set, get) => ({
  urqlClient: null,
  ...initialState,
  setIsFetching: (isFetching) =>
    set((state) => ({
      isFetching:
        typeof isFetching === "function"
          ? isFetching(state.isFetching)
          : isFetching,
    })),
  actions: {
    resetState: () => set(initialState),
    setInitialState: (data) => set(() => data),
    getWizardData: (selectedStep) => {
      const { language, projectId, nodeId, subType } = get();
      set({ isFetching: true });

      if (selectedStep === 0) {
        set({ step: 0, isFetching: false, isFormDirty: false });
        return;
      }

      fetchData({
        query: FetchWizardQry,
        variables: {
          input: {
            projectId,
            projectNodeId: nodeId,
            languageCode: language,
            step: selectedStep,
          },
        },
        onSuccess: ({ data }) => {
          const structuredTopology = data?.fetchWizard?.topology
            ? getTopology(data?.fetchWizard?.topology[0]?.configurations[0])
            : null;

          const {
            totalSteps,
            currentStep,
            languageCode,
            projectNodeId,
            availableSteps: availableStep,
            questionnaires,
          } = data?.fetchWizard || {};
          set({
            ...data?.fetchWizard,
            steps: totalSteps,
            step: currentStep,
            language: languageCode,
            nodeId: projectNodeId,
            availableSteps: availableStep,
            questionnaireLists: questionnaires,
            structuredTopology,
            computedLayoutTopology: getComputedLayout({
              structuredTopology,
              subType,
            }),
            possibleTopologyActions: data?.fetchWizard?.topology?.[0]?.actions
              ? data.fetchWizard.topology[0].actions.reduce((acc, item) => {
                  acc[item.id] = item;
                  return acc;
                }, {})
              : null,
            nodes: data?.fetchWizard?.nodes,
            rootNode: data?.fetchWizard?.nodes.filter((node) =>
              isWizardRootFolder(node),
            )[0],
          });
          //delay for animation
          setTimeout(() => {
            set({ isFetching: false });
          }, 300);
        },
        onError: (error) => set({ error }),
      });
    },
    goToPreviousStep: () => {
      const { step, actions, topology } = get();
      const topologyStatus = topology?.[0]?.status;

      if (topologyStatus === "Final" && step !== 1) {
        set({ step: 1 });
        actions.getWizardData(1);
      } else {
        const prevStep = Math.max(0, step - 1);
        set({ step: prevStep });
        actions.getWizardData(prevStep);
      }
    },
    goToNextStep: () => {
      const { step, actions, topology } = get();
      const wizardSubtype = get().subType;
      const topologyStatus = topology?.[0]?.status;

      if (topologyStatus === "Final" && wizardSubtype === "roomcontrol") {
        set({ step: 4 });
        actions.getWizardData(4);
      } else if (
        topologyStatus === "Final" &&
        wizardSubtype === "fireprotection"
      ) {
        set({ step: 5 });
        actions.getWizardData(5);
      } else if (
        topologyStatus === "Final" &&
        wizardSubtype === "roomcontrolwireless"
      ) {
        set({ step: 3 });
        actions.getWizardData(3);
      } else {
        const nextStep = step + 1;
        set({ step: nextStep });
        actions.getWizardData(nextStep);
      }
    },
    changeWizardStep: async ({ onCancelAction, answersLists }) => {
      const {
        language,
        projectId,
        subType,
        nodeId,
        step,
        availableSteps,
        urqlClient,
        actions: { getWizardData },
      } = get();

      // Wizard Create
      if (!nodeId) {
        set({ isFetching: true });
        const response = await urqlClient
          .mutation(AddWizardMutation, {
            input: { projectId, subType, languageCode: language },
          })
          .toPromise();
        if (response?.data?.addWizard) {
          const {
            totalSteps,
            currentStep,
            languageCode,
            projectNodeId,
            availableSteps: availableStep,
          } = response?.data?.addWizard || {};
          set({
            ...response?.data?.addWizard,
            steps: totalSteps,
            step: currentStep,
            language: languageCode,
            nodeId: projectNodeId,
            availableSteps: availableStep,
            isFetching: false,
          });

          const queryParamsStr = `?subType=${
            response.data.addWizard.subType
          }&projectId=${response.data.addWizard.projectId}&nodeId=${projectNodeId}`;
          window.history.replaceState({}, "", queryParamsStr);
        }
        return;
      }
      if (nodeId && step === 0) {
        getWizardData(1);
        return;
      }
      const action = async () => {
        try {
          set({ isFetching: true });

          const response = await urqlClient.mutation(WizardStepMut, {
            input: {
              step,
              nodeId,
              languageCode: language,
              projectId,
              questionnaires: answersLists,
            },
          });

          if (response?.data?.processWizardStep) {
            const {
              totalSteps,
              currentStep,
              languageCode,
              projectNodeId,
              availableSteps: availableStep,
            } = response?.data?.processWizardStep || {};

            const structuredTopology = response.data.processWizardStep.topology
              ? getTopology(
                  response.data.processWizardStep.topology[0]
                    ?.configurations[0],
                )
              : null;

            set({
              ...response.data.processWizardStep,
              questionnaireLists:
                response.data.processWizardStep.questionnaires,
              steps: totalSteps,
              step: currentStep,
              language: languageCode,
              nodeId: projectNodeId,
              availableSteps: availableStep,
              structuredTopology,
              computedLayoutTopology: getComputedLayout({
                structuredTopology,
                subType,
              }),
              possibleTopologyActions: response?.data?.processWizardStep
                ?.topology?.[0]?.actions
                ? response?.data?.processWizardStep?.topology[0].actions.reduce(
                    (acc, item) => {
                      acc[item.id] = item;
                      return acc;
                    },
                    {},
                  )
                : null,
              isFetching: false,
            });
          } else {
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };
      if (step > 1 && step !== availableSteps) {
        showConfirmModal({ action, onCancelAction });
      } else {
        action();
      }
    },
    setWizardQuestionsValidation: (data) => {
      set((prev) => ({
        wizardQuestionsValidation: {
          ...prev.wizardQuestionsValidation,
          ...data,
        },
      }));
    },
    updateWizardTitle: async ({ newTitle }) => {
      const { nodeId, language, projectId, setIsFetching } = get();

      fetchData({
        query: WizardUpdateDetailsMut,
        variables: {
          input: {
            nodeId,
            title: newTitle,
            languageCode: language,
            projectId,
          },
        },
        onSuccess: () => {
          set((prev) => {
            const rootNode =
              prev.nodes.filter((node) => isWizardRootFolder(node))[0] || {};

            rootNode.folderName = newTitle;

            return {
              nodes: prev.nodes,
              wizardTitle: newTitle,
              isFetching: false,
            };
          });
        },
        onError: (error) => {
          set({ isFetching: false });
          throw error;
        },
        setIsLoading: setIsFetching,
      });
    },
    updateZoneTitle: ({ newTitle, zoneId }) => {
      const { projectId, nodeId, language, topology, urqlClient, subType } =
        get();

      const action = async () => {
        try {
          const response = await urqlClient.mutation(WizardUpdateZoneMut, {
            input: {
              projectId,
              wizardNodeId: nodeId,
              zoneNodeId: zoneId,
              title: newTitle,
              languageCode: language,
            },
          });

          if (response?.data?.editZoneDetails) {
            set((prev) => {
              // TODO: Optimize recalc interactiveElementsArr
              const updatedInteractiveElementsArr =
                prev.interactiveElementsArr.map((btn) =>
                  btn.id === zoneId ? { ...btn, info: newTitle } : { ...btn },
                );

              // TODO: Discuss with AB for returning topology data but not only status and message fields
              const updatedTopology = updateTopologyZoneTitle(
                topology,
                zoneId,
                newTitle,
              );

              const structuredTopology = getTopology(
                updatedTopology[0]?.configurations[0],
              );

              return {
                interactiveElementsArr: updatedInteractiveElementsArr,
                topology: updatedTopology,
                structuredTopology,
                computedLayoutTopology: getComputedLayout({
                  structuredTopology,
                  subType,
                }),
                isFetching: false,
              };
            });
          } else {
            throw new Error("Wizard title was not updated");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      action();
    },
    invokeTopologyAction: async ({ action, sourceId, parameters }) => {
      const {
        projectId,
        nodeId,
        topology,
        selectedPasteItem,
        urqlClient,
        subType,
      } = get();

      const actionStore = async () => {
        try {
          set({ isFetching: true });

          const response = await urqlClient.mutation(TopologyActionsMut, {
            input: {
              wizardNodeId: nodeId,
              sourceId,
              projectId,
              action,
              parameters,
              targetIds: selectedPasteItem ? [...selectedPasteItem] : null,
              topology,
            },
          });

          if (response?.data?.invokeTopologyAction) {
            const updatedTopology =
              response?.data?.invokeTopologyAction?.Data.topology;

            const structuredTopology = updatedTopology
              ? getTopology(updatedTopology[0]?.configurations[0])
              : null;

            set({
              structuredTopology,
              topology: updatedTopology,
              computedLayoutTopology: getComputedLayout({
                structuredTopology,
                subType,
              }),
              topologyOperation: null,
              selectedPasteItem: null,
              isFetching: false,
            });
          } else {
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      await actionStore();
    },
    invokeTopologyCopyAction: async ({ action, sourceId }) => {
      const { topology, subType, language, urqlClient } = get();

      const actionStore = async () => {
        try {
          set({ isFetching: true });
          const response = await urqlClient.query(TopologyCopyAction, {
            input: {
              action,
              sourceId,
              subType,
              topology,
              languageCode: language,
            },
          });

          if (response?.data) {
            const updatedTopology = response?.data?.topologyCopyAction.Data[0];

            const structuredTopology = updatedTopology
              ? getTopology(updatedTopology?.configurations[0])
              : null;

            const computedLayoutTopology = getComputedLayout({
              structuredTopology,
              subType,
            });

            const daisyChainIFullness = {};

            computedLayoutTopology.interactiveElementsArr.forEach((elem) => {
              if (elem.interactiveKind === "pastBtn") {
                const current = daisyChainIFullness[elem.famGroupIdx] || [];
                daisyChainIFullness[elem.famGroupIdx] = [
                  ...new Set([...current, elem.topologyNodeId]),
                ];
              }
            });

            set({
              structuredTopology,
              computedLayoutTopology,
              famDaisyChainFullness: daisyChainIFullness,
              isFetching: false,
            });
          } else {
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      await actionStore();
    },
    updateWizardStepQuestionnaire: async ({ questionnaire }) => {
      const {
        language,
        projectId,
        nodeId,
        step,
        urqlClient,
        questionnaireLists,
        availableSteps,
        currentQuestionnaire,
      } = get();

      const action = async () => {
        set({ isFetching: true });
        try {
          const response = await urqlClient.mutation(
            updateWizardStepQuestionnaireMut,
            {
              input: {
                step,
                nodeId,
                languageCode: language,
                projectId,
                questionnaire: {
                  ...questionnaire[0],
                  title: questionnaire[0].title,
                  identifier: currentQuestionnaire.id,
                },
                questionnaireId: currentQuestionnaire.id,
              },
            },
          );

          if (response?.data?.modifyWizardStepQuestionnaire) {
            const { id: responseId, title } =
              response.data.modifyWizardStepQuestionnaire;

            const updatedQuestionnaireList = questionnaireLists.map((q) =>
              q.id === responseId
                ? {
                    ...q,
                    title,
                  }
                : q,
            );

            set((prev) => ({
              availableSteps: step,
              currentQuestionnaire: {
                ...prev.currentQuestionnaire,
                ...response?.data?.modifyWizardStepQuestionnaire,
              },
              questionnaireLists: updatedQuestionnaireList,
              isFetching: false,
            }));
          } else {
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      if (step !== availableSteps) {
        showConfirmModal({ action });
      } else {
        action();
      }
    },
    duplicateWizardStepQuestionnaire: async ({ duplicationFactor }) => {
      const {
        nodeId,
        step,
        projectId,
        language,
        urqlClient,
        availableSteps,
        questionnaireLists,
        currentQuestionnaire,
      } = get();

      const { id } = currentQuestionnaire;

      const action = async () => {
        set({ isDuplicationQuestionnaireFetching: true });
        try {
          const response = await urqlClient.mutation(
            duplicateWizardStepQuestionnaireMut,
            {
              input: {
                projectId,
                languageCode: language,
                step,
                nodeId,
                questionnaireId: id,
                duplicationFactor,
              },
            },
          );

          if (response?.data?.cloneWizardStepQuestionnaires) {
            const cloned = response?.data?.cloneWizardStepQuestionnaires;

            const newQuestionaires = insertItemAndUpdatePositions(
              questionnaireLists,
              cloned,
            );

            set((prev) => ({
              availableSteps: step,
              currentQuestionnaire: {
                ...prev.currentQuestionnaire,
                id: cloned[cloned.length - 1].id,
                position: cloned[cloned.length - 1].position,
              },
              questionnaireLists: newQuestionaires,
              isFetching: false,
              isDuplicationQuestionnaireFetching: false,
            }));
          } else {
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      if (step !== availableSteps) {
        showConfirmModal({ action });
      } else {
        action();
      }
    },
    deleteWizardStepQuestionnaire: async () => {
      const {
        nodeId,
        step,
        projectId,
        language,
        urqlClient,
        availableSteps,
        currentQuestionnaire,
        actions,
      } = get();

      const { id, position } = currentQuestionnaire;

      const action = async () => {
        set({ isFetching: true });
        try {
          const response = await urqlClient.mutation(
            deleteWizardStepQuestionnaireMut,
            {
              input: {
                projectId,
                languageCode: language,
                step,
                nodeId,
                questionnaireId: id,
              },
            },
          );

          if (response?.data?.removeWizardStepQuestionnaire) {
            set((prev) => {
              const newQuestionnaireLists = removeItemAndUpdatePositions({
                arr: prev.questionnaireLists,
                itemId: id,
              });

              // TODO check delete after replacing inside getSelectedQuestionnaire
              actions.getSelectedQuestionnaire({
                id: newQuestionnaireLists[
                  newQuestionnaireLists.length - 1 < position
                    ? newQuestionnaireLists.length - 1
                    : position
                ].id,
              });

              return {
                questionnaireLists: newQuestionnaireLists,
                isFetching: false,
              };
            });
          } else {
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      if (step !== availableSteps) {
        showConfirmModal({ action });
      } else {
        showConfirmModal({
          action,
          specificAlertText: translate("WIZARD_DELETE_ZONE_CONFIRMATION_ALERT"),
          specificSubmitText: translate(
            "WIZARD_DELETE_ZONE_CONFIRMATION_ACCEPT",
          ),
        });
      }
    },
    getSelectedQuestionnaire: async ({ id }) => {
      const { nodeId, step, urqlClient } = get();

      const action = async () => {
        set({ isFetching: true });
        try {
          // TODO replace with provided by AB query
          const response = await urqlClient.query(
            FetchWizardStepQuestionnaire,
            {
              input: { id, step, projectNodeId: nodeId },
            },
          );

          if (response?.data?.fetchWizardStepQuestionnaire) {
            set(() => ({
              availableSteps: step,
              currentQuestionnaire:
                response?.data?.fetchWizardStepQuestionnaire,
              isFetching: false,
            }));
          } else {
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      action();
    },
    createWizardStepQuestionnaire: async () => {
      const { nodeId, step, projectId, language, urqlClient, availableSteps } =
        get();

      const action = async () => {
        set({ isFetching: true });
        try {
          const response = await urqlClient.mutation(
            createWizardStepQuestionnaire,
            {
              input: { projectId, languageCode: language, step, nodeId },
            },
          );

          if (response?.data?.addWizardStepQuestionnaire) {
            set((prev) => ({
              availableSteps: step,
              currentQuestionnaire: response?.data?.addWizardStepQuestionnaire,
              questionnaireLists: [
                ...prev.questionnaireLists,
                response?.data?.addWizardStepQuestionnaire,
              ],
              isFetching: false,
            }));
          } else {
            set({ isFetching: false });
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      if (step !== availableSteps) {
        showConfirmModal({ action });
      } else {
        action();
      }
    },
    deleteWizardNode: async ({ sourceId, hasChildren, modalTitle }) => {
      const { projectId, nodeId, topology, urqlClient, subType } = get();

      const action = async () => {
        set({ isFetching: true });
        try {
          const response = await urqlClient.mutation(TopologyActionsMut, {
            input: {
              wizardNodeId: nodeId,
              sourceId,
              projectId,
              action: "deleteComponent",
              topology,
            },
          });

          if (response?.data?.invokeTopologyAction) {
            const updatedTopology =
              response?.data?.invokeTopologyAction?.Data.topology;

            const structuredTopology = updatedTopology
              ? getTopology(updatedTopology[0]?.configurations[0])
              : null;

            set({
              topology: updatedTopology,
              structuredTopology,
              computedLayoutTopology: getComputedLayout({
                structuredTopology,
                subType,
              }),
              isFetching: false,
            });
          } else {
            throw new Error("No questionnaire data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      showConfirmModal({
        action,
        title: translate("WIZARD_ATTENTION"),
        specificAlertText: hasChildren
          ? translate("WIZARD_NODE_DELETE_CHILDREN", modalTitle)
          : translate("WIZARD_NODE_DELETE", modalTitle),
        specificSubmitText: translate("DELETE"),
        specificCancelText: translate("CANCEL"),
      });
    },
    cancelWizardAction: () => {
      const { actions } = get();

      actions.getWizardData();
      set({
        topologyOperation: null,
        selectedPasteItem: null,
        famDaisyChainFullness: null,
      });
      // TODO: Store previous topology state data for avoid getWizardData request
      actions.getWizardData();
    },
    getCommissioningCost: async (configurationOption) => {
      const actionStore = async () => {
        const { projectId, nodeId, urqlClient } = get();
        try {
          set({ isFetching: true });
          const response = await urqlClient.query(ProjectCommissioningCalcQry, {
            input: { projectId, projectNodeId: nodeId, configurationOption },
          });

          if (response?.data) {
            set({
              projectCommissioningCost: {
                price: response?.data?.commissioningCalculation?.data.price,
                currency:
                  response?.data?.commissioningCalculation?.data.currency,
              },
              isFetching: false,
            });
          } else {
            throw new Error("No commissioning cost data received");
          }
        } catch (error) {
          set({ isFetching: false });
          throw error;
        }
      };

      await actionStore();
    },
    setWizardTitle: () => {
      const { nodes } = get();
      const newTitle =
        nodes?.filter((node) => isWizardRootFolder(node))[0].folderName ||
        "Error Wizard Name";

      set({ wizardTitle: newTitle });
    },
    setInteractiveElementsArr: (data) => {
      set(() => ({ interactiveElementsArr: data }));
    },
    toggleIsSecondSideBarOpen: () => {
      set((prev) => ({ isSecondSideBarOpen: !prev.isSecondSideBarOpen }));
    },
    toggleIsFirstSideBarOpen: () => {
      set((prev) => ({ isFirstSideBarOpen: !prev.isFirstSideBarOpen }));
    },
    setSelectedTopologyItemId: (id) => {
      set((prev) => {
        const selected = new Set(prev.selectedTopologyItemId);
        selected.has(id) ? selected.delete(id) : selected.add(id);
        return { selectedTopologyItemId: id };
      });
    },
    setSelectedTopologyItem: (item) => {
      set({ selectedTopologyItem: item });
    },

    handlePasteAction: (id, famGroupIdx) => {
      const { selectedPasteItem, famDaisyChainFullness } = get();
      const updatedSelected = new Set(selectedPasteItem || []);
      const updatedFulless = { ...famDaisyChainFullness };

      if (updatedSelected.has(id)) {
        updatedSelected.delete(id);

        if (famGroupIdx !== undefined && updatedFulless[famGroupIdx]) {
          updatedFulless[famGroupIdx] = updatedFulless[famGroupIdx].filter(
            (existingId) => existingId !== id,
          );
        }
      } else {
        if (famGroupIdx !== undefined) {
          const currentChainFullness = updatedFulless[famGroupIdx] || [];

          const selectedCount = Array.from(updatedSelected).filter(
            (selectedId) => updatedFulless[famGroupIdx].includes(selectedId),
          ).length;

          // TODO: Quick fix. EPF should provide fullness of FAM daisy chain in future (28.08.25)
          if (currentChainFullness.length + selectedCount >= 5) {
            showConfirmModal({
              isWithoutCancelBtn: true,
              title: translate("WIZARD_ATTENTION"),
              specificAlertText: translate("WIZARD_TOPOLOGY_MAX_FAM_WARNING"),
              specificSubmitText: translate("CANCEL"),
              action: () => {},
            });
            return;
          }
          updatedSelected.add(id);
        } else {
          updatedSelected.add(id);
        }
      }

      set({
        selectedPasteItem: updatedSelected,
      });
    },
    setTopology: (data) => {
      set(() => ({ topology: data }));
    },
    setInitialFamDaisyChainFullness: (famGroupIdx, id) => {
      set((prev) => {
        const current = prev.initialFamDaisyChainIFullness?.[famGroupIdx] || [];

        const updatedFullness = {
          ...prev.initialFamDaisyChainIFullness,
          [famGroupIdx]: [...new Set([...current, id])],
        };

        return { initialFamDaisyChainIFullness: updatedFullness };
      });
    },
    setTopologyOperation: (data) => {
      set(() => ({ topologyOperation: data }));
    },
    setCenterTopologyItemId: (id) => {
      set(() => ({ centerTopologyItemId: id }));
    },
    setWizardResultTab: (data) => {
      set(() => ({ wizardResultTab: data }));
    },
    setIsFormDirty: (isFormDirty) => {
      set(() => ({ isFormDirty }));
    },
    resetCommissionCost: () => {
      set(() => ({
        projectCommissioningCost: {
          price: null,
          currency: "",
        },
        isFetching: false,
      }));
    },
  },
}));

export const useWizardActions = () => useWizardStore((state) => state.actions);
export const useIsFetching = () => useWizardStore((state) => state.isFetching);
export const useIsDuplicationQuestionnaireFetching = () =>
  useWizardStore((state) => state.isDuplicationQuestionnaireFetching);
export const useWizardStep = () => useWizardStore((state) => state.step);
export const useCurrentQuestionnaire = () =>
  useWizardStore((state) => state.currentQuestionnaire);
export const useIsTopology = () =>
  useWizardStore((state) => state.topology && state.step === state.steps);

export const useMaximumQuestionnairesCount = () =>
  useWizardStore(
    (state) =>
      state.constraints?.find(
        ({ scope, reference }) =>
          scope[0] === "questionnaireLists" && reference === "count",
      )?.maxValue,
  );
export const useIsMultipleQuestionnaireListStep = () =>
  useMaximumQuestionnairesCount() > 1;

export const useWizardNodes = () => useWizardStore((state) => state.nodes);
export const useWizardTitle = () =>
  useWizardStore((state) => state.wizardTitle);
export const useSubType = () => useWizardStore((state) => state.subType);
export const useTopologyStatus = () =>
  useWizardStore((state) => state.topologyStatus);
export const useIsContinueDisabled = () =>
  useWizardStore((state) => state.isContinueDisabled);
export const useConstraints = () =>
  useWizardStore((state) => state.constraints);
export const useWizardQuestionsValidation = () =>
  useWizardStore((state) => state.wizardQuestionsValidation);

export const useAvailableSteps = () =>
  useWizardStore((state) => state.availableSteps);
export const useIsLastAvailableStep = () =>
  useWizardStore((state) => state.step === state.availableSteps);
export const useQuestionnaireLists = () =>
  useWizardStore((state) => state.questionnaireLists);

export const useSecondSideBarOpen = () =>
  useWizardStore((state) => state.isSecondSideBarOpen);

export const useFirstSideBarOpen = () =>
  useWizardStore((state) => state.isFirstSideBarOpen);

export const useSelectedTopologyItemId = () =>
  useWizardStore((state) => state.selectedTopologyItemId);

export const useSelectedTopologyItem = () =>
  useWizardStore((state) => state.selectedTopologyItem);

export const useInteractiveElementsArr = () =>
  useWizardStore((state) => state.interactiveElementsArr);

export const useSelectedPasteItem = () =>
  useWizardStore((state) => state.selectedPasteItem);

export const useTopologyOperation = () =>
  useWizardStore((state) => state.topologyOperation);

export const useStructuredTopology = () =>
  useWizardStore((state) => state.structuredTopology);

export const useWizardResultTab = () =>
  useWizardStore((state) => state.wizardResultTab);

export const useCenterTopologyItemId = () =>
  useWizardStore((state) => state.centerTopologyItemId);

export const useProjectCommissioningCost = () =>
  useWizardStore((state) => state.projectCommissioningCost);

export const useLanguage = () => useWizardStore((state) => state.language);
export const useProjectId = () => useWizardStore((state) => state.projectId);
export const useNodes = () => useWizardStore((state) => state.nodes);
export const useNodeId = () => useWizardStore((state) => state.nodeId);
export const useTopology = () => useWizardStore((state) => state.topology);
export const useIsFormDirty = () =>
  useWizardStore((state) => state.isFormDirty);
export const useStep = () => useWizardStore((state) => state.step);
export const usePossibleTopologyActions = () =>
  useWizardStore((state) => state.possibleTopologyActions);

export const useComputedTopologyLayout = () =>
  useWizardStore((state) => state.computedLayoutTopology);
